import type { RefreshOutcome } from '../layer'
import {
  DataTextureLayer,
  maxTextureSize,
  type Bbox3857,
  type DataTextureSpec,
  type TextureFrame,
} from './dataTextureLayer'
import { modelWidthTarget, pickWidth } from './modelLayer'
import { buildRamp } from './ramp'

/**
 * "Radar + next hour": the last hour of MRMS and the next one, on the map
 * (nowcast Stage E, docs/nowcast-plan.md §13).
 *
 * The worker draws every MRMS frame as a plate, and the newest one carried
 * forward along the motion field 5 to 60 minutes (`app/nowcast/plates.py`).
 * This is `DataTextureLayer` wired to that index the way `ModelLayer` wires it
 * to a run manifest, with three things a model plate does not need:
 *
 * * **Two classes.** Rain and snow are two halves of the index and two ramps,
 *   and the filter never blends across the split (`CLASS_GLSL`).
 * * **It moves while it is up.** A new frame every two minutes slides the
 *   whole loop, so `refresh()` swaps the frame list in place rather than the
 *   controller dropping and rebuilding the layer, which would blank it.
 * * **It says where observation ends** (`observedUntil`). The controller
 *   lands the slider there, marks it, and hides the observation layers past
 *   it: a held lightning frame under future radar reads as a lightning
 *   forecast.
 *
 * The plate holds a level on the "Next hour" bars' axis rather than a rate,
 * so the map and the section say the same thing in the same words.
 */

/** `/api/now/map`, as the worker wrote it. */
export interface RadarMapIndex {
  source: 'MRMS'
  time: string
  observed: { time: string; issue: string }[]
  forecast: { issue: string; time: string; leads: number[] } | null
  widths: number[]
  bbox3857: Bbox3857
  decode: { levels: number; split: number; uncovered: number; dry: number }
  ramp: { rain: [number, string][]; snow: [number, string][] }
  bands: {
    floor_mm_h: number
    rain_mm_h: [number, number]
    snow_mm_h: [number, number]
  }
  plate: string
}

/** The body is checked rather than cast: an older server, a proxy's page. */
function isIndex(body: unknown): body is RadarMapIndex {
  const b = body as Partial<RadarMapIndex> | null
  return (
    !!b &&
    b.source === 'MRMS' &&
    typeof b.time === 'string' &&
    Array.isArray(b.observed) &&
    Array.isArray(b.widths) &&
    Array.isArray(b.bbox3857) &&
    b.bbox3857.length === 4 &&
    typeof b.decode?.split === 'number' &&
    typeof b.decode?.uncovered === 'number' &&
    Array.isArray(b.ramp?.rain) &&
    Array.isArray(b.ramp?.snow) &&
    typeof b.plate === 'string'
  )
}

export async function fetchRadarMap(): Promise<RadarMapIndex | null> {
  const resp = await fetch('/api/now/map')
  if (!resp.ok) {
    // 404 is a deployment that does not draw it, which is an answer; a 503
    // before the first frame is one too. Anything else is worth a line.
    if (resp.status !== 404 && resp.status !== 503) {
      console.warn(`radar map: /api/now/map answered ${resp.status}`)
    }
    return null
  }
  const body: unknown = await resp.json()
  return isIndex(body) ? body : null
}

/** The legend reads the ramps and bands, which never move; one ask a page. */
let legendIndex: Promise<RadarMapIndex | null> | null = null
export function radarMapForLegend(): Promise<RadarMapIndex | null> {
  legendIndex ??= fetchRadarMap().catch(() => null)
  return legendIndex
}

/** Tests only. */
export function _resetRadarMapCache(): void {
  legendIndex = null
}

/**
 * The past loop's spacing: every other MRMS frame. Four minutes is
 * nowCOAST's own radar cadence, so the loop moves at the speed the radar
 * row beside it does, and the forecast's five sits next to it without the
 * pace visibly changing at now (decided 2026-10-24).
 */
export const OBSERVED_SPACING_MIN = 4

/**
 * The observed frames to draw, newest first and then reversed: the newest
 * always, and each earlier one at least `OBSERVED_SPACING_MIN` before the
 * last one taken, up to `count`.
 */
export function pickObserved(times: readonly Date[], count: number): Date[] {
  const sorted = [...times].sort((a, b) => b.getTime() - a.getTime())
  const out: Date[] = []
  for (const t of sorted) {
    if (out.length >= count) break
    const last = out[out.length - 1]
    if (!last || last.getTime() - t.getTime() >= OBSERVED_SPACING_MIN * 60_000 - 1000) out.push(t)
  }
  return out.reverse()
}

export function plateUrl(index: RadarMapIndex, issue: string, width: number, lead: number): string {
  return index.plate
    .replace('{issue}', issue)
    .replace('{width}', String(width))
    .replace('{lead}', String(lead).padStart(3, '0'))
}

/**
 * Which width to draw. MRMS is a 1 km grid, finer than any model here, so it
 * asks at twice the model target: the desktop takes the 2 km plate and a phone
 * the 4 km one, which is the working grid. Never past the driver's ceiling.
 */
export function pickRadarWidth(widths: readonly number[]): number | null {
  return pickWidth(widths, modelWidthTarget() * 2, maxTextureSize())
}

/** Both halves of the ramp, rain then snow: `levels` entries each. */
export function radarRamp(index: RadarMapIndex): Uint8Array {
  const { levels } = index.decode
  const rain = buildRamp(index.ramp.rain, 0, 1, levels)
  const snow = buildRamp(index.ramp.snow, 0, 1, levels)
  const out = new Uint8Array(rain.length + snow.length)
  out.set(rain)
  out.set(snow, rain.length)
  return out
}

/**
 * A forecast is offered only while the radar it was carried forward from is
 * this fresh: the line `/healthz/nowcast` turns red at. Past it the worker has
 * stopped or MRMS has, and the "next hour" is an hour that may already have
 * happened; the observed frames still show, under their own times.
 */
export const FORECAST_FRESH_MS = 15 * 60_000

export interface RadarMapFrames {
  spec: DataTextureSpec
  /** The newest observed instant: where the forecast begins. */
  observedUntil: Date | null
}

export function framesFor(
  index: RadarMapIndex,
  count: number,
  now: Date = new Date(),
): RadarMapFrames | null {
  const width = pickRadarWidth(index.widths)
  if (width === null || index.observed.length === 0) return null
  const byTime = new Map(index.observed.map((o) => [new Date(o.time).getTime(), o.issue]))
  const observed = pickObserved(
    [...byTime.keys()].map((t) => new Date(t)),
    count,
  )
  const frames: TextureFrame[] = observed.map((t) => ({
    time: t,
    url: plateUrl(index, byTime.get(t.getTime())!, width, 0),
  }))
  const newest = observed[observed.length - 1]
  let observedUntil: Date | null = null
  const forecast = index.forecast
  // Only the newest frame's forecast: an older one carried forward is a
  // forecast the newer frame has already overtaken.
  const fresh = now.getTime() - newest.getTime() <= FORECAST_FRESH_MS
  if (forecast && fresh && new Date(forecast.time).getTime() === newest.getTime()) {
    observedUntil = newest
    for (const lead of forecast.leads) {
      frames.push({
        time: new Date(newest.getTime() + lead * 60_000),
        url: plateUrl(index, forecast.issue, width, lead),
      })
    }
  }
  return {
    observedUntil,
    spec: {
      frames,
      bbox3857: index.bbox3857,
      ramp: radarRamp(index),
      // The first index that draws nothing: beyond the radars, then dry.
      nodata: index.decode.uncovered,
      split: index.decode.split,
    },
  }
}

/** What the plate says at a point, before it is put into words. */
export type RadarCell =
  | { kind: 'rain' | 'snow'; level: number }
  | { kind: 'dry' }
  | { kind: 'unknown' }

/** Index -> what it means, by the rule the worker wrote with it. */
export function decodeRadarIndex(value: number, decode: RadarMapIndex['decode']): RadarCell {
  if (value === decode.dry) return { kind: 'dry' }
  if (value >= decode.uncovered) return { kind: 'unknown' }
  const top = decode.levels - 1
  if (value < decode.split) return { kind: 'rain', level: value / top }
  return { kind: 'snow', level: (value - decode.split) / top }
}

const EARTH_RADIUS = 6378137

export interface RadarMapOptions {
  id: string
  attribution: string
  opacity: number
  /** How many observed frames: the nowCOAST budget, 12 or 6. */
  frameCount: number
}

export class RadarMapLayer extends DataTextureLayer {
  private index: RadarMapIndex | null = null
  private until: Date | null = null
  private readonly count: number

  constructor(opts: RadarMapOptions) {
    super({
      id: opts.id,
      attribution: opts.attribution,
      opacity: opts.opacity,
    })
    this.count = opts.frameCount
  }

  protected async loadFrames(): Promise<DataTextureSpec | null> {
    const index = await fetchRadarMap()
    if (!index) return null
    const found = framesFor(index, this.count)
    if (!found) return null
    this.index = index
    this.until = found.observedUntil
    return found.spec
  }

  /** The newest observed instant, when there is a forecast after it. */
  observedUntil(): Date | null {
    return this.until
  }

  /**
   * Ask for the index again and slide the loop to it.
   *
   * `patched` when the frames moved: the texture on screen stays up until
   * the controller's next `setTime` fetches the frame now at the cursor. A
   * layer that had nothing to draw until now was never added to the map, so
   * that one asks for a remount.
   */
  async refresh(): Promise<RefreshOutcome> {
    await this.load()
    const before = this.index?.time ?? null
    const wasEmpty = this.frames.length === 0
    let index: RadarMapIndex | null
    try {
      index = await fetchRadarMap()
    } catch {
      return 'unchanged'
    }
    // A failed ask keeps the loop on screen: it was right a moment ago.
    if (!index || index.time === before) return 'unchanged'
    const found = framesFor(index, this.count)
    if (!found) return 'unchanged'
    this.index = index
    this.until = found.observedUntil
    if (wasEmpty) {
      this.spec = found.spec
      this.frames = [...found.spec.frames]
      this.current = 0
      return 'remount'
    }
    this.replaceSpec(found.spec)
    return 'patched'
  }

  /**
   * What the plate on screen says at a point, or null when the frame at the
   * cursor is not the one on the GPU yet. The nearest texel — the cell — not
   * the blend the shader draws between cells: the same rule `/api/point`
   * keeps for a model plate (`formatReadout`).
   */
  cellAt(lon: number, lat: number): RadarCell | null {
    const index = this.index
    const frame = this.frames[this.current]
    const shown = this.shown
    if (!index || !frame || !shown || shown.url !== frame.url) return null
    const [west, south, east, north] = index.bbox3857
    const x = (EARTH_RADIUS * lon * Math.PI) / 180
    const y = EARTH_RADIUS * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))
    const u = (x - west) / (east - west)
    const v = (north - y) / (north - south)
    if (!(u >= 0 && u < 1 && v >= 0 && v < 1)) return { kind: 'unknown' }
    const { width, height, data } = shown.image
    const col = Math.min(width - 1, Math.floor(u * width))
    const row = Math.min(height - 1, Math.floor(v * height))
    return decodeRadarIndex(data[row * width + col], index.decode)
  }

  /** The rain bands, for putting a level back into in/h. */
  bands(): RadarMapIndex['bands'] | null {
    return this.index?.bands ?? null
  }
}

/** "Light", "Moderate", "Heavy": the band a level sits in. */
export function intensity(level: number): 'Light' | 'Moderate' | 'Heavy' {
  if (level < 1 / 3) return 'Light'
  return level < 2 / 3 ? 'Moderate' : 'Heavy'
}

/**
 * A rain level back to in/h: `mrms.level` inverted. Null at the top of the
 * ramp, which is "twice the heavy threshold or more" rather than a number.
 */
export function rainInPerHour(level: number, bands: RadarMapIndex['bands']): number | null {
  const [low, high] = bands.rain_mm_h
  const part = level * 3
  if (part >= 3 - 1e-6) return null
  let mm: number
  if (part < 1) mm = bands.floor_mm_h + part * (low - bands.floor_mm_h)
  else if (part < 2) mm = low + (part - 1) * (high - low)
  else mm = high + (part - 2) * high
  return Math.round((mm / 25.4) * 100) / 100
}

/**
 * The map menu's words for a cell. Snow gets words and no number, as it does
 * everywhere until there is a snow-to-liquid ratio (plan, "Snowfall rate").
 */
export function describeCell(
  cell: RadarCell,
  bands: RadarMapIndex['bands'] | null,
  forecast: boolean,
): string | null {
  const tail = forecast ? ' · forecast' : ''
  if (cell.kind === 'unknown') return null
  if (cell.kind === 'dry') return `No precipitation${tail}`
  const words = `${intensity(cell.level)} ${cell.kind}`
  if (cell.kind === 'snow' || !bands) return `${words}${tail}`
  const rate = rainInPerHour(cell.level, bands)
  const top = (2 * bands.rain_mm_h[1]) / 25.4
  const number = rate === null ? `over ${top.toFixed(2)} in/h` : `${rate.toFixed(2)} in/h`
  return `${words}, ${number}${tail}`
}
